import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, Video } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@bliks-io/ui';
import type { PartnerMaterial, PartnerMaterialLocale } from '@bliks-io/contracts';
import { usePartnerMaterials } from '@/pages/partner/api/queries/usePartnerMaterials';
import { PartnerErrorState } from '@/pages/partner/components/PartnerErrorState';
import { PartnerInfoPanel } from '@/pages/partner/components/PartnerInfoPanel';
import { PartnerLanguagePicker } from '@/pages/partner/components/PartnerLanguagePicker';
import { PartnerPageHeader } from '@/pages/partner/components/PartnerPageHeader';
import { toPartnerMaterialLocale } from '@/lib/partner-material-text';
import { InfoHubFilesTable } from './InfoHubFilesTable';
import { InfoHubVideoCard } from './InfoHubVideoCard';
import { PartnerSalesTabsNav } from '@/pages/partner/components/PartnerSalesTabsNav';

const isVideo = (material: PartnerMaterial): material is PartnerMaterial & { videoId: string } =>
  material.kind === 'video' && Boolean(material.videoId);

export function PartnerInfoHubPage() {
  const { t, i18n } = useTranslation();
  const {
    data: materials = [],
    isLoading,
    isError,
    error,
    isFetching,
    refetch
  } = usePartnerMaterials();
  // Follows the interface language until the partner picks one here, so a
  // language switch in the user settings reaches the open page too.
  const [chosenLocale, setLocale] = useState<PartnerMaterialLocale | null>(null);
  const locale = chosenLocale ?? toPartnerMaterialLocale(i18n.language);

  const files = materials.filter((material) => material.kind === 'file');
  const videos = materials.filter(isVideo);

  return (
    <div className="flex h-full flex-col">
      <PartnerSalesTabsNav />
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto p-6">
        <PartnerPageHeader
          title={t('partner.infoHub.title')}
          subtitle={t('partner.infoHub.subtitle')}
          onRefresh={() => refetch()}
          isRefreshing={isFetching}
        >
          <PartnerLanguagePicker value={locale} onChange={setLocale} />
        </PartnerPageHeader>

        {isError ? (
          <PartnerErrorState
            error={error}
            icon={FolderOpen}
            title={t('partner.infoHub.errorTitle')}
            description={t('partner.infoHub.errorDescription')}
            onRetry={() => refetch()}
          />
        ) : (
          <Tabs defaultValue="files">
            <TabsList>
              <TabsTrigger value="files">
                {t('partner.infoHub.tabs.files', { count: files.length })}
              </TabsTrigger>
              <TabsTrigger value="videos">
                {t('partner.infoHub.tabs.videos', { count: videos.length })}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="files" className="mt-4">
              <InfoHubFilesTable materials={files} locale={locale} isLoading={isLoading} />
            </TabsContent>

            <TabsContent value="videos" className="mt-4">
              {videos.length === 0 && !isLoading ? (
                <PartnerInfoPanel
                  icon={Video}
                  title={t('partner.infoHub.noVideos')}
                  className="py-16"
                />
              ) : (
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {videos.map((video) => (
                    <InfoHubVideoCard key={video._id} video={video} locale={locale} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
